import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccessTimeOutlined,
  BalanceOutlined,
  BusinessOutlined,
  CancelOutlined,
  CategoryOutlined,
  CheckCircleOutlineOutlined,
  HelpOutlineOutlined,
  InfoOutlined,
  LocationOnOutlined,
  TrendingUpOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import type { ComparativeSafetyIntelligencePayload, ReportVisual } from '../services/reportsApi';

export type ComparisonReadinessStatus = 'Limited' | 'Partial' | 'Available';

export interface ComparisonReadinessViewModel {
  hasData: boolean;
  statusLabel: ComparisonReadinessStatus;
  statusBody: string;
  calloutText: string;
  availableComparisonBody: string;
  timePeriodPill: string | null;
  auditContextNote: string | null;
  unavailableDimensions: Array<{ id: string; label: string }>;
  needsBullets: string[];
}

const UNAVAILABLE_DIMENSION_ORDER = ['zone', 'camera', 'site', 'process', 'shift', 'event_category'] as const;

const DIMENSION_LABELS: Record<string, string> = {
  zone: 'Zone',
  camera: 'Camera',
  site: 'Site',
  process: 'Process',
  shift: 'Shift',
  event_category: 'Event category',
};

function normalizeText(value?: string | null): string {
  return String(value || '').trim();
}

function toOptionalNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function visualItems(visual?: ReportVisual | null): Array<Record<string, any>> {
  return Array.isArray(visual?.data?.items) ? (visual?.data?.items as Array<Record<string, any>>) : [];
}

function rankedTimePatternItems(visual?: ReportVisual | null): Array<{ label: string; value: number }> {
  return visualItems(visual)
    .map((item) => ({
      label: normalizeText(String(item?.label || '')),
      value: toOptionalNumber(item?.value) ?? 0,
    }))
    .filter((item) => item.label && item.value > 0)
    .sort((a, b) => (b.value - a.value) || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function comparisonIsAvailable(comparison: { comparison_basis?: string | null }): boolean {
  const basis = normalizeText(comparison.comparison_basis).toLowerCase();
  return Boolean(basis) && basis !== 'not_available';
}

function deriveStatusLabel(
  data: ComparativeSafetyIntelligencePayload,
  availableComparisonCount: number,
): ComparisonReadinessStatus {
  const level = normalizeText(data.comparative_confidence?.level).toLowerCase();
  if (level === 'moderate' && availableComparisonCount >= 2) {
    return 'Partial';
  }
  if (availableComparisonCount >= 3) {
    return 'Available';
  }
  if (level === 'moderate' && availableComparisonCount >= 1) {
    return 'Partial';
  }
  return 'Limited';
}

function deriveStatusBody(statusLabel: ComparisonReadinessStatus): string {
  if (statusLabel === 'Available') {
    return 'Multiple comparable groups in this signed-off scan support best/worst comparison across retained dimensions.';
  }
  if (statusLabel === 'Partial') {
    return 'Some best/worst comparisons are available, but broader comparison remains limited within this signed-off scan.';
  }
  return 'Not enough comparable groups in this signed-off scan to support full best/worst comparison.';
}

function deriveCalloutText(
  data: ComparativeSafetyIntelligencePayload,
  comparisons: Array<{ comparison_basis?: string | null; dimension?: string | null }>,
): string {
  const topDrivers = Array.isArray(data.top_drivers) ? data.top_drivers : [];
  const availableCount = comparisons.filter((comparison) => comparisonIsAvailable(comparison)).length;
  const unavailableCount = comparisons.length - availableCount;
  const singleDriver = topDrivers.length === 1;
  const dominantPattern = singleDriver || (comparisons.length > 0 && unavailableCount >= comparisons.length - 1);

  if (dominantPattern) {
    return 'This signed-off scan identifies one dominant risk pattern. Broader best/worst comparison requires additional comparable zones, cameras, shifts, or sites, plus trusted monitored-observation time for each group.';
  }
  if (availableCount >= 2) {
    return 'Multiple comparable groups are present in this signed-off scan. Comparison strength still depends on monitored-observation time quality and retained grouping detail.';
  }
  return normalizeOptionalSummary(data.comparative_confidence?.summary)
    || 'Comparison readiness is limited until additional comparable groups and trusted monitored-observation time are available.';
}

function normalizeOptionalSummary(value?: string | null): string {
  const text = normalizeText(value);
  if (!text) return '';
  if (/comparative safety intelligence/i.test(text)) {
    return text.replace(/comparative safety intelligence/gi, 'comparison readiness');
  }
  return text;
}

function deriveTimePeriodPill(
  comparisons: Array<{
    dimension?: string | null;
    comparison_basis?: string | null;
    summary?: string | null;
    best_label?: string | null;
    worst_label?: string | null;
  }>,
  timePatternVisual?: ReportVisual | null,
): string | null {
  const timeComparison = comparisons.find(
    (comparison) => normalizeText(comparison.dimension).toLowerCase() === 'time_period',
  );
  if (!timeComparison || !comparisonIsAvailable(timeComparison)) {
    return null;
  }

  const rankedItems = rankedTimePatternItems(timePatternVisual);
  if (rankedItems.length >= 2) {
    const highest = rankedItems[0];
    const lowest = rankedItems[rankedItems.length - 1];
    return `${highest.label} highest (${highest.value}) vs ${lowest.label} (${lowest.value})`;
  }

  const summary = normalizeText(timeComparison.summary);
  const highestMatch = summary.match(/Highest activity:\s*(.+?)\s*\((\d+)\)/i);
  const lowestMatch = summary.match(/Lowest activity:\s*(.+?)\s*\((\d+)\)/i);
  if (highestMatch && lowestMatch) {
    return `${highestMatch[1]} highest (${highestMatch[2]}) vs ${lowestMatch[1]} (${lowestMatch[2]})`;
  }

  const worstLabel = normalizeText(timeComparison.worst_label);
  const bestLabel = normalizeText(timeComparison.best_label);
  if (worstLabel && bestLabel) {
    return `${worstLabel} vs ${bestLabel}`;
  }
  return null;
}

function deriveUnavailableDimensions(
  comparisons: Array<{ dimension?: string | null; comparison_basis?: string | null }>,
): Array<{ id: string; label: string }> {
  const unavailable = new Set(
    comparisons
      .filter((comparison) => !comparisonIsAvailable(comparison))
      .map((comparison) => normalizeText(comparison.dimension).toLowerCase())
      .filter(Boolean),
  );

  return UNAVAILABLE_DIMENSION_ORDER
    .filter((dimension) => unavailable.has(dimension))
    .map((dimension) => ({
      id: dimension,
      label: DIMENSION_LABELS[dimension] || dimension,
    }));
}

function deriveNeedsBullets(data: ComparativeSafetyIntelligencePayload): string[] {
  const missingInputs = Array.isArray(data.comparative_confidence?.missing_inputs)
    ? data.comparative_confidence?.missing_inputs || []
    : [];
  const cleaned = missingInputs
    .map((item) => normalizeText(item))
    .filter(Boolean);
  if (cleaned.length) {
    return cleaned;
  }
  return [
    'More comparable zones/cameras/shifts/sites',
    'Trusted monitored-observation time for each group',
  ];
}

export function buildComparisonReadinessViewModel(
  data?: ComparativeSafetyIntelligencePayload | null,
  denominatorTrust?: string | null,
  timePatternVisual?: ReportVisual | null,
): ComparisonReadinessViewModel {
  if (!data) {
    return {
      hasData: false,
      statusLabel: 'Limited',
      statusBody: 'Signed-off comparative appendix was not included in this package.',
      calloutText: 'Comparison readiness requires comparative data from the signed-off artifact.',
      availableComparisonBody: 'Limited time-period comparison is not available from this signed-off scan.',
      timePeriodPill: null,
      auditContextNote: null,
      unavailableDimensions: UNAVAILABLE_DIMENSION_ORDER.map((dimension) => ({
        id: dimension,
        label: DIMENSION_LABELS[dimension],
      })),
      needsBullets: [
        'More comparable zones/cameras/shifts/sites',
        'Trusted monitored-observation time for each group',
      ],
    };
  }

  const comparisons = Array.isArray(data.best_worst_comparisons) ? data.best_worst_comparisons : [];
  const availableComparisonCount = comparisons.filter((comparison) => comparisonIsAvailable(comparison)).length;
  const statusLabel = deriveStatusLabel(data, availableComparisonCount);
  const timePeriodPill = deriveTimePeriodPill(comparisons, timePatternVisual);
  const trust = normalizeText(denominatorTrust).toLowerCase();
  const auditContextNote = trust && trust !== 'trusted'
    ? 'Counts are audit context only.'
    : null;

  return {
    hasData: true,
    statusLabel,
    statusBody: deriveStatusBody(statusLabel),
    calloutText: deriveCalloutText(data, comparisons),
    availableComparisonBody: timePeriodPill
      ? 'Time-period comparison is available from signed-off event counts.'
      : 'Limited time-period comparison is not available from this signed-off scan.',
    timePeriodPill,
    auditContextNote,
    unavailableDimensions: deriveUnavailableDimensions(comparisons),
    needsBullets: deriveNeedsBullets(data),
  };
}

function statusBadgeSx(statusLabel: ComparisonReadinessStatus) {
  if (statusLabel === 'Available') {
    return { bgcolor: '#ECFDF5', color: '#047857', borderColor: '#A7F3D0' };
  }
  if (statusLabel === 'Partial') {
    return { bgcolor: '#FFFBEB', color: '#B45309', borderColor: '#FDE68A' };
  }
  return { bgcolor: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' };
}

function dimensionIcon(dimensionId: string) {
  switch (dimensionId) {
    case 'zone':
      return LocationOnOutlined;
    case 'camera':
      return VideocamOutlined;
    case 'site':
      return BusinessOutlined;
    case 'process':
      return CategoryOutlined;
    case 'shift':
      return AccessTimeOutlined;
    default:
      return CategoryOutlined;
  }
}

function ComparisonMetricCard(props: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  children: React.ReactNode;
}) {
  const Icon = props.icon;
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: '#E5E7EB',
        borderRadius: 2.5,
        bgcolor: '#fff',
        p: 1.6,
        minWidth: 0,
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            bgcolor: props.iconBg,
            color: props.iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          <Icon aria-hidden sx={{ fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {props.title}
        </Typography>
      </Stack>
      {props.children}
    </Box>
  );
}

export function ComparisonReadinessSection(props: {
  data?: ComparativeSafetyIntelligencePayload | null;
  denominatorTrust?: string | null;
  timePatternVisual?: ReportVisual | null;
}) {
  const model = buildComparisonReadinessViewModel(props.data, props.denominatorTrust, props.timePatternVisual);
  const badgeSx = statusBadgeSx(model.statusLabel);

  return (
    <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #E5E7EB', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.4} alignItems="flex-start">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: '#EFF6FF',
                color: '#2563EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <BalanceOutlined aria-hidden />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Comparison status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                What can and cannot be compared from this Signed Safety Scan package.
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={model.statusLabel}
            size="small"
            sx={{
              fontWeight: 700,
              border: '1px solid',
              ...badgeSx,
            }}
          />
        </Stack>

        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: '#EFF6FF',
            border: '1px solid #BFDBFE',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <InfoOutlined aria-hidden sx={{ fontSize: 18, color: '#2563EB', mt: '2px' }} />
            <Typography variant="body2" sx={{ color: '#1E3A8A' }}>
              {model.calloutText}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          <ComparisonMetricCard
            title="Status"
            icon={CheckCircleOutlineOutlined}
            iconColor="#047857"
            iconBg="#ECFDF5"
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              {model.statusLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {model.statusBody}
            </Typography>
          </ComparisonMetricCard>

          <ComparisonMetricCard
            title="Comparison available"
            icon={TrendingUpOutlined}
            iconColor="#2563EB"
            iconBg="#EFF6FF"
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: model.timePeriodPill ? 1 : 0 }}>
              {model.availableComparisonBody}
            </Typography>
            {model.timePeriodPill ? (
              <Chip
                icon={<AccessTimeOutlined sx={{ fontSize: '16px !important' }} />}
                label={model.timePeriodPill}
                size="small"
                sx={{
                  bgcolor: '#EFF6FF',
                  color: '#1D4ED8',
                  border: '1px solid #BFDBFE',
                  fontWeight: 600,
                  maxWidth: '100%',
                  height: 'auto',
                  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                }}
              />
            ) : null}
            {model.auditContextNote ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.85 }}>
                {model.auditContextNote}
              </Typography>
            ) : null}
          </ComparisonMetricCard>

          <ComparisonMetricCard
            title="Not comparable yet"
            icon={CancelOutlined}
            iconColor="#B91C1C"
            iconBg="#FEF2F2"
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These comparison views are not yet supported by the retained signed-off data.
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {model.unavailableDimensions.length ? model.unavailableDimensions.map((dimension) => {
                const Icon = dimensionIcon(dimension.id);
                return (
                  <Chip
                    key={dimension.id}
                    icon={<Icon sx={{ fontSize: '16px !important' }} />}
                    label={dimension.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      bgcolor: '#F8FAFC',
                      borderColor: '#E2E8F0',
                      color: '#475569',
                    }}
                  />
                );
              }) : (
                <Typography variant="caption" color="text.secondary">
                  No unavailable dimensions were listed in the signed-off comparative appendix.
                </Typography>
              )}
            </Stack>
          </ComparisonMetricCard>

          <ComparisonMetricCard
            title="What is still needed"
            icon={HelpOutlineOutlined}
            iconColor="#7C3AED"
            iconBg="#F5F3FF"
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Additional comparable data is required before stronger comparison claims can be made.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.1 }}>
              {model.needsBullets.map((bullet) => (
                <Typography key={bullet} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.35 }}>
                  {bullet}
                </Typography>
              ))}
            </Box>
          </ComparisonMetricCard>
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2, color: 'text.secondary' }}>
          <InfoOutlined aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
          <Typography variant="caption">
            Comparative results are only as strong as the quality and comparability of the underlying data.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
